import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { getSlingShifts, getSlingLocationsSrcIdId, useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { SlingShift, SlingShiftsSummary } from '../api/generated/model';
import { useQuery } from '@tanstack/react-query';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

// Import DataTable system
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';


type ShiftColumnKey =
  | 'id' | 'start_time' | 'end_time' | 'user_name' | 'status' | 'type' | 'has_event';

const SlingLocationDetailPage: React.FC = () => {
  const { srcId, locationId } = useParams<{ srcId: string; locationId: string }>();
  const { user } = useSelector((state: RootState) => state.auth);

  // Ref for scroll container
  const cardBodyRef = useRef<HTMLDivElement>(null);

  // Fetch user preferences
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: {
      enabled: !!user?.id,
    }
  });

  // Fetch location details
  const { data: locationData } = useQuery({
    queryKey: ['sling-location', srcId, locationId],
    queryFn: () => getSlingLocationsSrcIdId(srcId || '', parseInt(locationId || '0', 10)),
    enabled: !!srcId && !!locationId,
  });

  // Sort state
  const [sortField, setSortField] = useState<ShiftColumnKey>('start_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filter state
  const [filters, setFilters] = useState({
    no_event: undefined as string | undefined,
  });

  // Fixed limit for infinite scroll
  const FETCH_LIMIT = 50;

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((shift: SlingShift) => shift.id || '', []);

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            src_id: srcId,
            location_id: locationId ? parseInt(locationId, 10) : undefined,
            sort_field: sortField as any,
            sort_direction: sortDirection,
            has_event: filters.no_event ? false : undefined,
            limit: FETCH_LIMIT,
          },
    [srcId, locationId, sortField, sortDirection, filters.no_event]
  );

  const {
    allItems: shifts,
    totalCount,
    apiSummary: shiftsSummary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
  } = usePaginatedQuery<SlingShift, Awaited<ReturnType<typeof getSlingShifts>>, SlingShiftsSummary>({
    queryKey: 'sling-shifts',
    buildQueryParams,
    queryFn: (params) => getSlingShifts(params as any),
    getRowKey,
    enabled: !!srcId && !!locationId,
  });

  // Handle sorting
  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field as ShiftColumnKey);
    setSortDirection(direction);
  };

  // Handle clicking on summary card to toggle filter
  const handleNoEventFilterToggle = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      no_event: prev.no_event ? undefined : 'true'
    }));
  }, []);

  // Format date/time for display with timezone
  const formatDateTime = (dateStr: string | undefined, timezone?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };

    // Use the location's timezone if provided, otherwise use local timezone
    if (timezone) {
      options.timeZone = timezone;
    }

    return date.toLocaleString('en-US', options);
  };

  // Define columns
  const columns: ColumnConfig<SlingShift>[] = useMemo(() => [
    {
      key: 'user_name',
      label: 'User',
      sortable: true,
      width: 220,
      renderCell: (shift) => {
        const userName = [shift.user?.first_name, shift.user?.last_name].filter(Boolean).join(' ');
        const hasEvent = shift.has_event;

        return (
          <div className="d-flex align-items-center gap-2">
            {!hasEvent && (
              <i
                className="bi bi-exclamation-triangle-fill text-warning"
                title="Not associated with any event"
              ></i>
            )}
            <span>{userName || shift.user?.email || 'Unknown'}</span>
          </div>
        );
      },
      tooltip: (shift) => {
        const userName = [shift.user?.first_name, shift.user?.last_name].filter(Boolean).join(' ');
        const eventStatus = shift.has_event ? 'Associated with event' : 'NOT associated with any event';
        return `${userName || 'Unknown'} - ${eventStatus}`;
      },
    },
    {
      key: 'start_time',
      label: 'Start Time',
      sortable: true,
      width: 200,
      renderCell: (shift) => formatDateTime(shift.start_time, shift.location?.timezone),
      tooltip: (shift) => formatDateTime(shift.start_time, shift.location?.timezone),
    },
    {
      key: 'end_time',
      label: 'End Time',
      sortable: true,
      width: 200,
      renderCell: (shift) => formatDateTime(shift.end_time, shift.location?.timezone),
      tooltip: (shift) => formatDateTime(shift.end_time, shift.location?.timezone),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 150,
      renderCell: (shift) => (
        <span className={`badge bg-${shift.status === 'published' ? 'success' : 'secondary'}`}>
          {shift.status || 'N/A'}
        </span>
      ),
      tooltip: (shift) => shift.status || 'N/A',
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: 150,
      renderCell: (shift) => shift.type || 'N/A',
      tooltip: (shift) => shift.type || 'N/A',
    },
    {
      key: 'has_event',
      label: 'Event Status',
      sortable: true,
      width: 150,
      renderCell: (shift) => {
        const hasEvent = shift.has_event;
        const eventIds = shift.event_ids;

        if (hasEvent && eventIds && eventIds.length > 0) {
          return (
            <span className="badge bg-success">
              <i className="bi bi-check-circle me-1"></i>
              {eventIds.length === 1 ? 'Has Event' : `${eventIds.length} Events`}
            </span>
          );
        }

        return (
          <span className="badge bg-warning">
            <i className="bi bi-exclamation-triangle me-1"></i>
            No Event
          </span>
        );
      },
      tooltip: (shift) => {
        return shift.has_event && shift.event_ids?.length
          ? `Associated with ${shift.event_ids.length} event(s)`
          : 'This shift is not associated with any event';
      },
    },
    {
      key: 'id',
      label: 'Shift ID',
      sortable: true,
      width: 200,
      renderCell: (shift) => (
        <a
          href={`https://app.getsling.com/shifts/details/${shift.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none fw-semibold"
          onClick={(e) => e.stopPropagation()}
        >
          {shift.id}
        </a>
      ),
      tooltip: (shift) => `Click to view in Sling: ${shift.id}`,
    },
  ], []);

  // Calculate summary stats
  const displayTotal = totalCount ?? shifts.length;

  // Summary card content
  const stickyHeaderContent = (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-calendar-event fs-1 text-primary"></i>
          </div>
          <h3 className="mb-1">{displayTotal}</h3>
          <p className="text-muted mb-0 small">Total Shifts</p>
        </Card.Body>
      </Card>
      <Card
        className={`h-100 summary-card ${filters.no_event ? 'summary-card-active' : ''}`}
        onClick={handleNoEventFilterToggle}
        style={{ cursor: 'pointer' }}
      >
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-calendar-x fs-1 text-warning"></i>
          </div>
          <h3 className="mb-1">{shiftsSummary?.shifts_without_events ?? 0}</h3>
          <p className="text-muted mb-0 small">Shifts w/o Events</p>
        </Card.Body>
      </Card>
    </div>
  );

  // Location name, address, and timezone for breadcrumb/title
  const locationName = locationData?.name || locationId;
  const locationAddress = locationData?.address;
  const locationTimezone = locationData?.timezone;

  // Build subtitle with address and timezone
  const subtitle = (() => {
    if (locationAddress && locationTimezone) {
      return `${locationAddress} | ${locationTimezone}`;
    }
    if (locationAddress) {
      return locationAddress;
    }
    if (locationTimezone) {
      return locationTimezone;
    }
    return 'Viewing all shifts for this location';
  })();

  return (
    <DashboardLayout
      title={`Sling Location: ${locationName}`}
      subtitle={subtitle}
      actions={
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => window.open(`https://app.getsling.com/manage/locations/${locationId}`, '_blank', 'noopener,noreferrer')}
        >
          Open in Sling
        </Button>
      }
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      <style>{`
        /* Clickable summary card styling */
        .summary-card {
          transition: all 0.2s ease;
          border: 2px solid transparent !important;
        }

        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        .summary-card-active {
          border: 2px solid #0d6efd !important;
          background-color: rgba(13, 110, 253, 0.05);
        }
      `}</style>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {(error as any)?.message || 'Failed to load shifts'}
            </Alert>
          </Col>
        </Row>
      )}

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
            <Card.Header className="bg-primary text-white flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                    <span className="text-primary fw-semibold">
                      <i className="bi bi-calendar-event me-1"></i>
                      Sling Shifts
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="bg-header-control rounded px-3 py-1">
                    <span className="text-primary">
                      {displayTotal > 0 ? (
                        <>{displayTotal} total shifts</>
                      ) : (
                        'No shifts found'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body
              ref={cardBodyRef}
              className="flex-grow-1 overflow-hidden"
              style={{
                padding: 0,
                minHeight: 0,
              }}
            >
              <DataTable
                data={shifts}
                columns={columns}
                getRowKey={getRowKey}
                sortConfig={{ field: sortField, direction: sortDirection }}
                onSortChange={handleSortChange}
                pagination={{
                  hasMore,
                  limit: FETCH_LIMIT,
                }}
                onLoadMore={handleLoadMore}
                isLoading={isLoading}
                preferencesKey="sling-location-detail-shifts"
                userId={user?.id}
                userPreferences={userData?.preferences}
                maxHeight="100%"
                enableColumnReorder={true}
                enableColumnResize={true}
                enableColumnVisibility={true}
                enableStickyHeader={true}
              />
            </Card.Body>
      </Card>
    </DashboardLayout>
  );
};

export default SlingLocationDetailPage;
